import React, { useState, useEffect } from 'react';
import { CSSTransition } from 'react-transition-group';

const CartDetail = ({ 
  cart, 
  categories, 
  currency, 
  onClose, 
  onRemoveFromCart, 
  onUpdateQuantity, 
  calculateItemPrice,
  calculateTotalPrice,
  onGenerateOrderSheet
}) => {
  const [showContent, setShowContent] = useState(false);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    // 加载时的动画效果
    const timer = setTimeout(() => {
      setShowContent(true);
    }, 50);

    const animateTimer = setTimeout(() => {
      setAnimate(true);
    }, 100);

    return () => {
      clearTimeout(timer);
      clearTimeout(animateTimer);
    }
  }, []);

  // 外部点击时关闭
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // 计算购物车总数量
  const calculateTotalItems = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  return (
    <div 
      className="bg-black bg-opacity-50 fixed inset-0 z-50 flex items-end justify-center sm:items-center"
      onClick={handleOverlayClick}
    >
      <div 
        className={`relative w-full max-w-lg bg-white/90 backdrop-blur-md rounded-t-2xl sm:rounded-2xl shadow-xl overflow-hidden transition-all duration-300 transform ${
          showContent ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
        }`}
        style={{ maxHeight: '85vh' }}
      >
        {/* 标题和关闭按钮 */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-indigo-50/90 to-white/90 backdrop-blur-sm px-4 py-3 border-b border-gray-100/80 flex justify-between items-center">
          <h2 className="font-semibold text-gray-800 text-lg flex items-center">
            <svg className="w-5 h-5 mr-2 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            购物车
            <span className="ml-2 bg-indigo-100 text-indigo-700 text-xs px-2 py-0.5 rounded-full">
              {calculateTotalItems()} 件商品
            </span>
          </h2>
          <button 
            onClick={onClose}
            className="backdrop-blur-sm bg-gray-50/80 rounded-full p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100/80 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 购物车内容 */}
        <div className="overflow-y-auto p-4 py-2 bg-gradient-to-br from-indigo-50/30 via-blue-50/30 to-white/30 pb-14" style={{ maxHeight: 'calc(85vh - 140px)' }}>
          {cart.length === 0 ? (
            <div className="text-center py-10">
              <div className="mx-auto w-20 h-20 text-gray-400 mb-4">
                <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <p className="text-gray-500">购物车是空的</p>
              <button 
                onClick={onClose}
                className="mt-4 bg-indigo-600/90 backdrop-blur-sm text-white px-4 py-2 rounded-lg hover:bg-indigo-700/90 transition-colors text-sm"
              >
                继续点餐
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {cart.map((item, index) => (
                <CSSTransition
                  key={item.id}
                  in={animate}
                  timeout={300}
                  classNames="popup"
                  appear
                >
                  <div 
                    className="bg-white/80 backdrop-blur-sm rounded-xl shadow-sm py-2 px-3 transition-all duration-300"
                    style={{ transitionDelay: `${index * 50}ms` }}
                  >
                    <div className="flex items-start">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-800">{item.dish.name}</h3>
                        <p className="text-xs text-gray-500 italic">{item.dish.rawName}</p>
                        {item.selectedOption && (
                          <p className="text-xs text-gray-600 mt-1 bg-gray-100/50 inline-block px-2 py-0.5 rounded">
                          选择：{item.selectedOption.translation_title}
                          </p>
                        )}
                        {item.selectedAddon && (
                          <p className="text-xs text-gray-600 mt-1 ml-1 bg-gray-100/50 inline-block px-2 py-0.5 rounded">
                            附加: {item.selectedAddon.translation_title}
                          </p>
                        )}
                        
                        <div className="flex items-center justify-between mt-1">
                          <div className="flex items-center bg-gray-50/80 rounded-lg">
                            <button
                              onClick={() => onUpdateQuantity(item.id, -1)}
                              className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-indigo-600"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                              </svg>
                            </button>
                            <span className="w-8 text-center text-sm">{item.quantity}</span>
                            <button
                              onClick={() => onUpdateQuantity(item.id, 1)}
                              className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-indigo-600"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                            </button>
                          </div>
                          <span className="text-indigo-600 font-medium">¥{calculateItemPrice(item)}</span>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => onRemoveFromCart(item.id)}
                        className="ml-2 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </CSSTransition>
              ))}
            </div>
          )}
        </div>

        {/* 底部结算区域 */}
        <div className={`sticky bottom-0 bg-white/90 backdrop-blur-sm px-4 py-4 border-t border-gray-100/80 transition-all duration-300 transform ${
          showContent ? 'translate-y-0' : 'translate-y-8'
        }`}>
          <div className="flex justify-between items-center mb-3">
            <span className="text-gray-700">总计</span>
            <span className="text-xl font-semibold text-indigo-600">¥{calculateTotalPrice()}</span>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onGenerateOrderSheet}
              disabled={cart.length === 0}
              className="flex-1 backdrop-blur-sm bg-indigo-600/90 text-white py-3 rounded-xl hover:bg-indigo-700/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              生成点菜单
            </button>
            <button
              onClick={onClose}
              className="px-4 py-3 backdrop-blur-sm border border-gray-200/80 text-gray-700 rounded-xl hover:bg-gray-50/80 transition-all duration-300 flex items-center justify-center"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartDetail; 